import { useCallback, useEffect, useState } from "react";
import api from "../../api/axios";
import { UsersAPI, type UserDto } from "../../api/usersApi";
import { Avatar, Banner, ROLES, curMonth, errText, fmtPhone, monthLabel, shiftMonth } from "./cabinet-ui";
import { EarningsTab, OverviewTab, SalaryTab } from "./SheetTabs";
import TeamTab from "./TeamTab";
import ProfileTab from "./ProfileTab";

import "../../styles/profile/cabinet.css";

type Tab = "overview" | "earnings" | "salary" | "team" | "profile";

function MonthNav({ month, onChange }: { month: string; onChange: (m: string) => void }) {
  const isCur = month === curMonth();
  return (
    <div className="cb-month">
      <button type="button" onClick={() => onChange(shiftMonth(month, -1))} aria-label="Предыдущий месяц">‹</button>
      <span>{monthLabel(month)}</span>
      <button type="button" onClick={() => onChange(shiftMonth(month, 1))} disabled={isCur} aria-label="Следующий месяц">›</button>
      {!isCur && <button type="button" className="cb-month-now" onClick={() => onChange(curMonth())}>Сегодня</button>}
    </div>
  );
}

export default function ProfilePage() {
  const [user, setUser] = useState<UserDto | null>(null);
  const [userErr, setUserErr] = useState("");
  const [tab, setTab] = useState<Tab>("overview");
  const [month, setMonth] = useState(curMonth());
  const [me, setMe] = useState<any>(null);
  const [sheetErr, setSheetErr] = useState("");
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    try { setUser(await UsersAPI.me()); setUserErr(""); }
    catch (e) { setUserErr(errText(e, "Не удалось загрузить профиль")); }
  }, []);

  const loadSheet = useCallback(async () => {
    setLoading(true);
    try {
      setMe((await api.get(`/payroll/me?month=${month}`)).data);
      setSheetErr("");
    } catch (e) {
      setSheetErr(errText(e, "Не удалось загрузить данные за месяц"));
    } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { loadUser(); }, [loadUser]);
  useEffect(() => { loadSheet(); }, [loadSheet]);

  if (userErr && !user) return <div className="cb"><Banner kind="err" onClose={loadUser}>{userErr}. Нажмите ✕, чтобы повторить.</Banner></div>;
  if (!user) return <div className="cb"><div className="cb-empty">Загрузка…</div></div>;

  const tabs: { id: Tab; label: string; show: boolean }[] = [
    { id: "overview", label: "Обзор", show: true },
    { id: "earnings", label: "Заработок", show: true },
    { id: "salary", label: "Зарплата", show: true },
    { id: "team", label: me?.canManage ? "Сотрудники" : "Команда", show: !!me?.hasTeam },
    { id: "profile", label: "Профиль", show: true },
  ];
  const current: Tab = tabs.find((t) => t.id === tab && t.show) ? tab : "overview";
  const needsSheet = current === "overview" || current === "earnings" || current === "salary";

  return (
    <div className="cb">
      <header className="cb-head">
        <div className="cb-head-who">
          <Avatar url={user.avatarUrl} first={user.firstName} last={user.lastName} size={56} />
          <div>
            <h2>{user.lastName} {user.firstName}</h2>
            <div className="cb-sub"><span className="cb-role">{ROLES[user.role] ?? user.role}</span>{user.phone ? ` · ${fmtPhone(user.phone)}` : ""}</div>
          </div>
        </div>
        {current !== "profile" && <MonthNav month={month} onChange={setMonth} />}
      </header>

      <nav className="cb-tabs" role="tablist" aria-label="Разделы личного кабинета">
        {tabs.filter((t) => t.show).map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={current === t.id} className={current === t.id ? "active" : ""}
            onClick={(e) => { setTab(t.id); e.currentTarget.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" }); }}>{t.label}</button>
        ))}
      </nav>

      {needsSheet && sheetErr && <Banner kind="err" onClose={loadSheet}>{sheetErr}. Нажмите ✕, чтобы повторить.</Banner>}
      {needsSheet && loading && !me && !sheetErr && <div className="cb-empty">Загрузка…</div>}

      {current === "overview" && me && <OverviewTab sheet={me} />}
      {current === "earnings" && me && <EarningsTab sheet={me} />}
      {current === "salary" && me && <SalaryTab sheet={me} onChanged={loadSheet} />}
      {current === "team" && me?.hasTeam && <TeamTab month={month} canManage={!!me.canManage} meId={user.id} />}
      {current === "profile" && <ProfileTab user={user} onChanged={loadUser} />}
    </div>
  );
}
